import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToday, useMonth, useProgress, addDaysISO, monthKey, monthLabel, daysInMonth, formatLongDate, isoToday } from "@/hooks/useCalendar";
import { Clock, Flame as Fire, CheckCircle2, Circle, ChevronLeft, ChevronRight } from "lucide-react";

const SLOT_ORDER = ["breakfast", "lunch", "dinner", "snack"];

export default function Meals() {
  return <CalendarView kind="meal" title="Meals" heading="A day, plated" />;
}

/**
 * Calendar-driven pillar view.
 * kind ∈ "meal" | "movement" | "coaching"
 */
export function CalendarView({ kind, title, heading }) {
  const { data: today, loading: todayLoading } = useToday();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  const initialDate = params.get("date") || (today?.active_date ?? isoToday());
  const [selectedDate, setSelectedDate] = useState(initialDate);
  useEffect(() => {
    if (!params.get("date") && today?.active_date) {
      setSelectedDate(today.active_date);
    }
  }, [today?.active_date]);

  const currentMonth = monthKey(selectedDate);
  const { data: month, loading: monthLoading } = useMonth(currentMonth);
  const { has, toggle } = useProgress();

  const dayData = useMemo(() => {
    if (!month) return {};
    if (kind === "meal") {
      const recipes = (month.recipes || []).filter((r) => r.date === selectedDate);
      return { recipes };
    }
    if (kind === "movement") {
      const movements = (month.movements || []).filter((m) => m.date === selectedDate);
      return { movements };
    }
    return { coaching: (month.coaching || []).find((c) => c.date === selectedDate) };
  }, [month, selectedDate, kind]);

  if (todayLoading || monthLoading || !today || !month) {
    return <div className="label-eyebrow" data-testid="calendar-loading">Loading…</div>;
  }

  // Pre-launch gate
  if (today.is_before_launch) {
    return <PreLaunchGate today={today} kind={kind} title={title} />;
  }

  const changeDate = (newIso) => {
    setSelectedDate(newIso);
    setParams({ date: newIso });
  };

  const daysCount = daysInMonth(currentMonth);
  const [year, mn] = currentMonth.split("-").map(Number);
  const dayList = Array.from({ length: daysCount }, (_, i) =>
    `${year}-${String(mn).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`
  );

  return (
    <div className="space-y-12" data-testid={`page-${kind}`}>
      <header>
        <div className="label-eyebrow mb-3">{title} · {month.month.label}</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">{heading}</h1>
        {month.month.theme && (
          <p className="mt-6 text-stone-600 italic font-serif text-2xl max-w-2xl">
            Theme: <span className="text-[hsl(var(--primary))]">{month.month.theme}</span>
          </p>
        )}
      </header>

      <MonthNav currentMonth={currentMonth} onChange={(mk) => changeDate(`${mk}-01`)} />

      <DayStrip
        days={dayList}
        selected={selectedDate}
        onSelect={changeDate}
        kind={kind}
        has={has}
      />

      <div className="text-sm text-stone-500 italic">{formatLongDate(selectedDate)}</div>

      {kind === "meal" && (
        <MealsDay
          recipes={dayData.recipes}
          date={selectedDate}
          done={has(selectedDate, "meal")}
          onToggle={() => toggle(selectedDate, "meal")}
        />
      )}
      {kind === "movement" && (
        <MovementDay
          movements={dayData.movements}
          date={selectedDate}
          user={user}
          tierNotes={month.month.tier_notes || {}}
          done={has(selectedDate, "movement")}
          onToggle={() => toggle(selectedDate, "movement")}
        />
      )}
      {kind === "coaching" && (
        <CoachingDay
          coaching={dayData.coaching}
          date={selectedDate}
          monthTheme={month.month.theme}
          done={has(selectedDate, "coaching")}
          onToggle={() => toggle(selectedDate, "coaching")}
        />
      )}
    </div>
  );
}

function PreLaunchGate({ today, kind, title }) {
  return (
    <div className="space-y-10" data-testid={`gate-${kind}`}>
      <header>
        <div className="label-eyebrow mb-3">{title}</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">
          The <em className="italic text-[hsl(var(--primary))]">door</em> opens December 1, 2026.
        </h1>
      </header>
      <div className="border-t border-b border-border grid md:grid-cols-3 gap-0">
        <div className="p-10 border-r border-border">
          <div className="label-eyebrow mb-3">Countdown</div>
          <div className="font-serif text-6xl">{today.days_until_launch}</div>
          <div className="text-stone-500 mt-2 italic">day{today.days_until_launch === 1 ? "" : "s"} until launch</div>
        </div>
        <div className="p-10 border-r border-border">
          <div className="label-eyebrow mb-3">Opening theme</div>
          <div className="font-serif text-2xl leading-tight italic">
            {today.month.theme || "Root: Foundation & Reset"}
          </div>
          <div className="text-sm text-stone-500 mt-3">Month one · {today.month.label}</div>
        </div>
        <div className="p-10">
          <div className="label-eyebrow mb-3">While you wait</div>
          <p className="text-stone-600 leading-relaxed text-sm">
            Your practice begins December 1, 2026. Recipes, movement, and coaching are being prepared — a new month, one at a time.
          </p>
        </div>
      </div>
    </div>
  );
}

function MonthNav({ currentMonth, onChange }) {
  const [y, m] = currentMonth.split("-").map(Number);
  const prev = new Date(y, m - 2, 1);
  const next = new Date(y, m, 1);
  const prevKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
  const nextKey = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
  return (
    <div className="flex items-center justify-between border-b border-border pb-4" data-testid="month-nav">
      <button onClick={() => onChange(prevKey)} data-testid="month-prev" className="flex items-center gap-2 text-sm text-stone-500 hover:text-[hsl(var(--foreground))] transition-colors">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> {monthLabel(prevKey)}
      </button>
      <div className="font-serif text-2xl">{monthLabel(currentMonth)}</div>
      <button onClick={() => onChange(nextKey)} data-testid="month-next" className="flex items-center gap-2 text-sm text-stone-500 hover:text-[hsl(var(--foreground))] transition-colors">
        {monthLabel(nextKey)} <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

function DayStrip({ days, selected, onSelect, kind, has }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="day-strip">
      {days.map((iso) => {
        const dayNum = parseInt(iso.slice(-2), 10);
        const done = has(iso, kind);
        const active = iso === selected;
        return (
          <button
            key={iso}
            onClick={() => onSelect(iso)}
            data-testid={`day-${dayNum}`}
            className={`px-3 py-2 text-xs border transition-colors flex items-center gap-1.5 ${
              active
                ? "border-[hsl(var(--foreground))] bg-[hsl(var(--foreground))] text-[hsl(var(--background))]"
                : "border-border hover:border-stone-500"
            }`}
          >
            {done ? <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} /> : <Circle className="w-3 h-3" strokeWidth={1.5} />}
            {String(dayNum).padStart(2, "0")}
          </button>
        );
      })}
    </div>
  );
}

function MealsDay({ recipes, date, done, onToggle }) {
  if (!recipes || recipes.length === 0) {
    return (
      <div className="border-t border-border pt-12 text-center" data-testid="meals-empty">
        <div className="font-serif italic text-2xl text-stone-500">No recipes for {date} yet.</div>
        <p className="text-sm text-stone-500 mt-3">Check back soon — the coach is still cooking.</p>
      </div>
    );
  }
  const bySlot = SLOT_ORDER.map((slot) => ({ slot, items: recipes.filter((r) => r.meal_type === slot) }));
  return (
    <article className="border-t border-border" data-testid="meal-day">
      <div className="grid md:grid-cols-4 gap-0">
        {bySlot.map(({ slot, items }) => (
          <section key={slot} className="p-6 border-r last:border-r-0 border-b md:border-b-0 border-border">
            <div className="label-eyebrow mb-3">{slot}</div>
            {items.length === 0 ? (
              <p className="text-sm italic text-stone-400">— none —</p>
            ) : (
              items.map((m) => <RecipeCard key={m.id} recipe={m} />)
            )}
          </section>
        ))}
      </div>
      <div className="p-6 border-t border-border flex justify-end">
        <button onClick={onToggle} data-testid="btn-mark-meal" className={`text-xs uppercase tracking-widest px-6 py-3 border transition-colors ${done ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border hover:border-stone-500"}`}>
          {done ? "Meal honoured" : "Mark meal complete"}
        </button>
      </div>
    </article>
  );
}

function RecipeCard({ recipe: m }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("rr_token");
  const photoUrl = m.photo_path ? `${backend}/api/files/${m.photo_path}?auth=${token}` : null;
  return (
    <div className="mb-6 last:mb-0" data-testid={`recipe-${m.id}`}>
      {photoUrl && (
        <div className="aspect-[4/3] overflow-hidden mb-3">
          <img src={photoUrl} alt={m.name} className="w-full h-full object-cover" />
        </div>
      )}
      <h3 className="font-serif text-xl leading-tight mb-3">{m.name}</h3>
      {(m.prep_min > 0 || m.kcal > 0) && (
        <div className="flex items-center gap-4 text-xs text-stone-500 mb-4">
          {m.prep_min > 0 && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} />{m.prep_min} min</span>}
          {m.kcal > 0 && <span className="inline-flex items-center gap-1"><Fire className="w-3 h-3" strokeWidth={1.5} />{m.kcal} kcal</span>}
        </div>
      )}
      {m.ingredients?.length > 0 && (
        <ul className="text-sm text-stone-700 space-y-1 mb-3">
          {m.ingredients.map((i, idx) => <li key={idx}>— {i}</li>)}
        </ul>
      )}
      {m.directions && (
        <div className="border-t border-border pt-3 mt-2">
          <div className="label-eyebrow mb-1.5">How to make it</div>
          <p className="text-sm text-stone-600 leading-relaxed">{m.directions}</p>
        </div>
      )}
    </div>
  );
}

const LEVEL_TO_TIER = { new: "softening", casual: "softening", regular: "expanding", advanced: "awakening" };
const TIER_META = {
  softening: { label: "Softening", desc: "Beginner · gentler tempo, fewer sets." },
  expanding: { label: "Expanding", desc: "Intermediate · the steady core practice." },
  awakening: { label: "Awakening", desc: "Advanced · extra set, sharper edge." },
};

function MovementDay({ movements, date, user, tierNotes, done, onToggle }) {
  const defaultTier = LEVEL_TO_TIER[user?.onboarding?.fitness_level] || "expanding";
  const [tier, setTier] = useState(defaultTier);
  useEffect(() => setTier(defaultTier), [defaultTier]);

  if (!movements || movements.length === 0) {
    return (
      <div className="border-t border-border pt-12 text-center" data-testid="movement-empty">
        <div className="font-serif italic text-2xl text-stone-500">No movement scheduled for {date} yet.</div>
      </div>
    );
  }
  const byTier = Object.fromEntries(movements.map((m) => [m.tier, m]));
  const current = byTier[tier] || movements[0];
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("rr_token");
  const photoUrl = current.photo_path ? `${backend}/api/files/${current.photo_path}?auth=${token}` : null;

  return (
    <div className="space-y-8" data-testid="exercise-day">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-border" data-testid="tier-selector">
        {(["softening", "expanding", "awakening"]).map((t) => (
          <button
            key={t}
            onClick={() => setTier(t)}
            data-testid={`tier-${t}`}
            disabled={!byTier[t]}
            className={`p-6 border-r last:border-r-0 border-border text-left transition-colors ${tier === t ? "bg-[hsl(var(--primary))] text-white" : !byTier[t] ? "opacity-40" : "hover:bg-[hsl(var(--muted))]"}`}
          >
            <div className={`label-eyebrow mb-2 ${tier === t ? "text-white/70" : ""}`}>Tier</div>
            <div className="font-serif text-2xl mb-1">{TIER_META[t].label}</div>
            <div className={`text-xs leading-relaxed ${tier === t ? "text-white/80" : "text-stone-500"}`}>{tierNotes[t] || TIER_META[t].desc}</div>
          </button>
        ))}
      </div>

      <article className="grid md:grid-cols-3 gap-0 border-t border-border">
        <section className="p-8 md:col-span-1 border-r border-border">
          {photoUrl && (
            <div className="aspect-[4/5] overflow-hidden mb-6">
              <img src={photoUrl} alt={current.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="label-eyebrow mb-2 text-[hsl(var(--primary))]">{TIER_META[tier].label} · {current.focus}</div>
          <h3 className="font-serif text-3xl leading-[1.05] mb-4">{current.title}</h3>
          {current.note && <p className="text-xs italic text-stone-500 mb-4 leading-relaxed">{current.note}</p>}
          <p className="text-sm text-stone-600 mb-6"><Clock className="inline w-4 h-4 mr-1" strokeWidth={1.25} />{current.duration_min} minutes</p>
          <button onClick={onToggle} data-testid="btn-mark-exercise" className={`text-xs uppercase tracking-widest px-6 py-3 border transition-colors ${done ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border hover:border-stone-500"}`}>
            {done ? "Movement honoured" : "Mark movement done"}
          </button>
        </section>
        <section className="md:col-span-2 p-8">
          <div className="label-eyebrow mb-4">The Sequence</div>
          <ol className="divide-y divide-border">
            {(current.moves || []).map((m, i) => (
              <li key={i} className="py-4">
                <div className="flex items-baseline justify-between gap-6">
                  <div>
                    <span className="font-serif text-2xl mr-4 text-stone-400">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-xl">{m.name}</span>
                  </div>
                  <div className="text-sm text-stone-500 whitespace-nowrap">
                    {m.sets > 1 && <span>{m.sets} sets · </span>}{m.reps}
                  </div>
                </div>
                {m.form_cue && <p className="text-xs text-stone-500 mt-2 pl-14 italic leading-relaxed">↳ {m.form_cue}</p>}
              </li>
            ))}
          </ol>
        </section>
      </article>
    </div>
  );
}

function CoachingDay({ coaching, date, monthTheme, done, onToggle }) {
  if (!coaching) {
    return (
      <div className="border-t border-border pt-12 text-center" data-testid="coaching-empty">
        <div className="font-serif italic text-2xl text-stone-500">No session for {date} yet.</div>
      </div>
    );
  }
  return (
    <article className="grid md:grid-cols-12 gap-0 border-t border-border" data-testid="coaching-day">
      <section className="md:col-span-5 p-10 md:p-12 bg-[hsl(var(--foreground))] text-[hsl(var(--background))]">
        {monthTheme && <div className="label-eyebrow text-stone-400 mb-4">This month's theme</div>}
        {monthTheme && <div className="font-serif text-2xl italic mb-10">{monthTheme}</div>}
        <div className="label-eyebrow text-stone-400 mb-4">Affirmation</div>
        <p className="font-serif italic text-3xl md:text-4xl leading-[1.15]">"{coaching.affirmation}"</p>
      </section>
      <section className="md:col-span-7 p-10 md:p-12">
        <div className="label-eyebrow mb-3">{coaching.title}</div>
        <div className="mb-8">
          <div className="label-eyebrow text-stone-400 mb-2">Reflect</div>
          <p className="font-serif text-2xl md:text-3xl leading-snug">{coaching.reflection}</p>
        </div>
        <div className="mb-10 border-t border-border pt-6">
          <div className="label-eyebrow text-stone-400 mb-2">Today's action</div>
          <p className="text-base leading-relaxed">{coaching.action}</p>
        </div>
        <button onClick={onToggle} data-testid="btn-mark-session" className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest px-6 py-3 border transition-colors ${done ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border hover:border-stone-500"}`}>
          {done ? "Session honoured" : "Mark session complete"}
        </button>
      </section>
    </article>
  );
}
